import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Row,
  Slider,
  Space,
  Spin,
  Switch,
  Typography,
  message as toast,
} from 'antd';
import dayjs from 'dayjs';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { WATERMARK_CHANGED_EVENT } from 'components/watermark';
import { watermarkBackground } from 'components/watermark/render';
import { NAV_GROUPS } from 'layouts/nav-config';
import { colors } from 'theme/tokens';
import { WatermarkConfigView, watermarkError } from './types';

interface FormValue {
  appEnabled: boolean;
  appClasses: string[];
  adminEnabled: boolean;
  fields: string[];
  opacityPct: number;
  hiddenLayer: boolean;
  /** Vị trí app ĐANG BẬT (lưu thì đổi thành danh sách tắt). */
  appOnPositions: string[];
  /** Nhóm menu trang Quản trị ĐANG BẬT. */
  adminOnGroups: string[];
}

/** Vị trí trên trang Quản trị: Tổng quan + từng nhóm menu (nav-config là nguồn duy nhất). */
const ADMIN_POSITIONS = [
  { value: 'home', label: 'Tổng quan' },
  ...NAV_GROUPS.map((group) => ({ value: group.key, label: group.label })),
];

const FIELD_OPTIONS = [
  { value: 'showName', label: 'Tên hiển thị' },
  { value: 'showTiktokId', label: 'TikTok ID (@username)' },
  { value: 'showTiktokUid', label: 'UID TikTok' },
  { value: 'showPhoneTail', label: '4 số cuối điện thoại' },
  { value: 'showTime', label: 'Ngày giờ (cập nhật mỗi phút)' },
];
const FIELD_KEYS = FIELD_OPTIONS.map((option) => option.value);

const toForm = (data: WatermarkConfigView): FormValue => ({
  appEnabled: data.appEnabled,
  appClasses: data.appClasses,
  adminEnabled: data.adminEnabled,
  fields: FIELD_KEYS.filter((key) => (data as any)[key]),
  opacityPct: data.opacityPct,
  hiddenLayer: data.hiddenLayer,
  appOnPositions: data.meta.appPositions
    .map((item) => item.value)
    .filter((key) => !data.appOffPositions.includes(key)),
  adminOnGroups: ADMIN_POSITIONS.map((item) => item.value).filter(
    (key) => !data.adminOffGroups.includes(key)
  ),
});

/** Khung giả lập một trang có nội dung để xem watermark trông thế nào. */
const PreviewPanel = ({
  config,
  value,
}: {
  config: WatermarkConfigView;
  value: Partial<FormValue>;
}) => {
  const fields = value.fields ?? [];
  const has = (key: string) => fields.includes(key);
  const { parts, code } = config.preview;
  const first = [has('showName') && parts.name, has('showTiktokId') && parts.tiktokId]
    .filter(Boolean)
    .join(' · ');
  const second = [has('showTiktokUid') && parts.tiktokUid, has('showPhoneTail') && parts.phoneTail]
    .filter(Boolean)
    .join(' · ');
  const opacity = (value.opacityPct ?? config.meta.opacityPct.default) / 100;
  const background = watermarkBackground(
    {
      enabled: true,
      lines: [first, second].filter(Boolean),
      code,
      showTime: has('showTime'),
      opacity,
      hiddenLayer: !!value.hiddenLayer,
      hiddenOpacity: config.meta.hiddenOpacity,
    },
    new Date()
  );

  const bars = [92, 70, 84, 56, 76, 64];
  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 12,
        border: `1px solid ${colors.line}`,
        background: colors.surface,
        overflow: 'hidden',
        minHeight: 300,
      }}
    >
      <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18 }}>
          <div style={{ width: 40, height: 40, borderRadius: 20, background: colors.accentSoft }} />
          <div style={{ flex: 1 }}>
            <div
              style={{
                height: 12,
                width: '40%',
                borderRadius: 6,
                background: colors.textPrimary,
                opacity: 0.8,
              }}
            />
            <div
              style={{
                height: 10,
                width: '25%',
                borderRadius: 5,
                background: colors.textMuted,
                marginTop: 8,
              }}
            />
          </div>
        </div>
        {bars.map((width, index) => (
          <div
            key={index}
            style={{
              height: 10,
              width: `${width}%`,
              borderRadius: 5,
              background: index % 3 === 0 ? colors.textSecondary : colors.line,
              marginBottom: 12,
            }}
          />
        ))}
        {/* Mảng tối: kiểm tra chữ còn thấy trên ảnh/video nền tối. */}
        <div style={{ height: 90, borderRadius: 10, background: '#1B2150', marginTop: 16 }} />
      </div>
      {background ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundImage: background.image,
            backgroundSize: background.size,
            backgroundRepeat: 'repeat',
          }}
        />
      ) : null}
    </div>
  );
};

/** Cài đặt watermark — ADMIN sửa, MOD chỉ xem (backend chặn bằng 400 WATERMARK_ADMIN_ONLY). */
const SettingsTab = () => {
  const { isAdmin, loaded } = useCurrentRoles();
  const readOnly = loaded && !isAdmin;
  const [config, setConfig] = useState<WatermarkConfigView | null>(null);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<FormValue>();
  const watched = Form.useWatch([], form) as Partial<FormValue> | undefined;

  const apply = (data: WatermarkConfigView) => {
    setConfig(data);
    form.setFieldsValue(toForm(data));
  };

  useEffect(() => {
    AxiosUtils.get(API.watermarkConfig)
      .then((res: any) => apply(res.data))
      .catch((error) => setLoadError(watermarkError(error)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    const v = await form.validateFields();
    setSaving(true);
    try {
      const res: any = await AxiosUtils.put(API.watermarkConfig, {
        appEnabled: v.appEnabled,
        appClasses: v.appClasses,
        adminEnabled: v.adminEnabled,
        ...Object.fromEntries(FIELD_KEYS.map((key) => [key, v.fields.includes(key)])),
        opacityPct: v.opacityPct,
        hiddenLayer: v.hiddenLayer,
        appOffPositions: config!.meta.appPositions
          .map((item) => item.value)
          .filter((key) => !v.appOnPositions.includes(key)),
        // Chỉ gửi khoá nhóm đang có trong menu; khoá cũ không còn thì bỏ.
        adminOffGroups: ADMIN_POSITIONS.map((item) => item.value).filter(
          (key) => !v.adminOnGroups.includes(key)
        ),
      });
      apply(res.data);
      window.dispatchEvent(new Event(WATERMARK_CHANGED_EVENT));
      toast.success('Đã lưu. App cập nhật khi người dùng mở lại app (tối đa vài phút).');
    } catch (error) {
      toast.error(watermarkError(error));
    } finally {
      setSaving(false);
    }
  };

  const previewValue = useMemo(() => watched ?? (config ? toForm(config) : {}), [watched, config]);

  if (loadError) return <Alert type="error" showIcon message={loadError} />;
  if (!config) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} xl={12}>
        <Card>
          {readOnly ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Tài khoản MOD chỉ xem được cài đặt. Nhờ Admin nếu cần đổi."
            />
          ) : null}
          <Form form={form} layout="vertical" disabled={readOnly} requiredMark={false}>
            <Typography.Title level={5} style={{ marginTop: 0 }}>
              Phủ ở đâu
            </Typography.Title>
            <Form.Item name="appEnabled" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Switch checkedChildren="App: bật" unCheckedChildren="App: tắt" />
            </Form.Item>
            <Form.Item
              name="appClasses"
              label="Ai bị phủ trên app"
              extra="Phân lớp giống Chính sách nhắn tin. Mặc định chỉ nội bộ; bật cả fan thì màn công khai cũng có watermark."
            >
              <Checkbox.Group
                options={config.meta.classes.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
                disabled={readOnly || watched?.appEnabled === false}
              />
            </Form.Item>
            <Form.Item
              name="adminEnabled"
              valuePropName="checked"
              extra="Phủ mọi trang Quản trị của Admin/MOD (kể cả hộp thoại, bảng xuất)."
            >
              <Switch
                checkedChildren="Trang Quản trị: bật"
                unCheckedChildren="Trang Quản trị: tắt"
              />
            </Form.Item>

            <Typography.Title level={5}>In gì lên màn hình</Typography.Title>
            <Form.Item
              name="fields"
              extra="Mã truy vết (vd 3F7-K2M) luôn được in — tra ra đúng tài khoản kể cả khi tên bị che. Số điện thoại là dữ liệu cá nhân, chỉ bật khi thật cần."
            >
              <Checkbox.Group options={FIELD_OPTIONS} style={{ display: 'grid', gap: 8 }} />
            </Form.Item>

            <Typography.Title level={5}>Độ mờ của chữ</Typography.Title>
            <Form.Item
              name="opacityPct"
              extra="Số càng nhỏ chữ càng mờ. Mặc định 5% — nhìn lướt gần như không thấy, nhưng ảnh chụp màn hình vẫn đọc được (tab Soi ảnh làm rõ thêm). Dưới 4% thì ảnh bị thu nhỏ hoặc chụp lại bằng điện thoại khác khó đọc."
            >
              <Slider
                min={config.meta.opacityPct.min}
                max={config.meta.opacityPct.max}
                marks={{
                  [config.meta.opacityPct.min]: 'Rất mờ',
                  5: '5%',
                  8: '8%',
                  [config.meta.opacityPct.max]: 'Rõ',
                }}
                tooltip={{ formatter: (v) => `${v}%` }}
              />
            </Form.Item>
            <Form.Item
              name="hiddenLayer"
              valuePropName="checked"
              extra="Mặc định tắt. Bật thì thêm một lớp mã chữ to, nghiêng ngược, rất mờ — dự phòng khi kẻ lộ cắt/che lớp chữ chính (soi bằng tab Soi ảnh), nhưng trên màn hình sẽ thấy hai hướng chữ chồng nhau. Ảnh bị nén qua ứng dụng chat hoặc chụp bằng điện thoại khác thường mất lớp này."
            >
              <Switch checkedChildren="Lớp mã ẩn: bật" unCheckedChildren="Lớp mã ẩn: tắt" />
            </Form.Item>

            <Typography.Title level={5}>Vị trí có watermark</Typography.Title>
            <Form.Item
              name="appOnPositions"
              label="Trên app"
              extra="Bỏ tick = khu vực đó không có watermark. Nên giữ Ứng dụng, Công việc, Doanh thu, Tin nhắn — nơi có thông tin nội bộ."
            >
              <Checkbox.Group
                options={config.meta.appPositions}
                style={{ display: 'grid', gap: 8 }}
                disabled={readOnly || watched?.appEnabled === false}
              />
            </Form.Item>
            <Form.Item name="adminOnGroups" label="Trên trang Quản trị (theo nhóm menu)">
              <Checkbox.Group
                options={ADMIN_POSITIONS}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                  gap: 8,
                }}
                disabled={readOnly || watched?.adminEnabled === false}
              />
            </Form.Item>
          </Form>
          <Space style={{ marginTop: 8 }} wrap>
            <Button type="primary" onClick={save} loading={saving} disabled={readOnly}>
              Lưu cài đặt
            </Button>
            {config.updatedAt ? (
              <Typography.Text type="secondary">
                Sửa lần cuối {dayjs(config.updatedAt).format('HH:mm DD/MM/YYYY')}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">Đang dùng cấu hình mặc định</Typography.Text>
            )}
          </Space>
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <Card title="Xem trước (bằng chính tài khoản của bạn)">
          <PreviewPanel config={config} value={previewValue} />
          <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
            Trên app, chữ xếp so le và nghiêng như trên; mọi màn hình (kể cả hộp thoại, trang Ứng
            dụng Chỉ xem, phòng LIVE) đều bị phủ. Không phủ được hộp thoại của hệ điều hành (chọn
            ngày, chọn ảnh, thông báo hệ thống) và video xem toàn màn hình trong trang web nhúng.
          </Typography.Paragraph>
        </Card>
      </Col>
    </Row>
  );
};

export default SettingsTab;
